import { useRef, useState } from 'react';

import {
  Box,
  Col,
  getCSSVariable,
  getTokenValue,
  Link,
  PressableBox,
  Text,
} from '@opendoor/bricks-next';
import type { BoxProps, TextProps } from '@opendoor/bricks-next';
import { IconCheck, IconChevronRight } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';

import type { ILpComponentList, ILpComponentListItem } from '../../declarations/contentful';
import Container from './shared/Container';
import Sheet from './shared/Sheet';
import { BNColorThemes, getBNComponentThemeColors } from './shared/styles/ComponentTheme';
import type { IBNComponentThemeConfig } from './shared/styles/ComponentTheme';
import { Desc, Eyebrow, TitleWithAccent } from './shared/Typography';
import { markdownToReactElement } from './shared/utils';

const ListItemIcon = ({
  isNumbered,
  hasCheckmark,
  isLight,
  index,
  iconBg,
  iconColor,
}: {
  isNumbered: boolean;
  hasCheckmark: boolean;
  isLight: boolean;
  index: number;
  iconBg: string;
  iconColor: string;
}) => {
  if (!isNumbered && !hasCheckmark) return null;

  const size = isLight ? 40 : 24;

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      borderRadius="$2x"
      backgroundColor={iconBg as BoxProps['backgroundColor']}
      width={size}
      height={size}
      minWidth={size}
      minHeight={size}
      mt="$half"
      flexShrink={0}
    >
      {isNumbered ? (
        <Text
          tag="span"
          typography="$bodyLarge"
          fontWeight="$medium"
          color={iconColor as TextProps['color']}
        >
          {index + 1}
        </Text>
      ) : (
        <IconCheck size={16} color={iconColor} strokeWidth={2} />
      )}
    </Box>
  );
};

interface ItemDetailProps {
  item: ILpComponentListItem;
  theme: IBNComponentThemeConfig;
  listItemTitleSize: '18px' | '24px';
}

const ItemDetails = ({ item, theme = {} as IBNComponentThemeConfig }: ItemDetailProps) => {
  return (
    <Col
      position="relative"
      pr={item.fields.url ? '$10x' : '$0'}
      gap="$2"
      minWidth="$0"
      flexShrink={1}
    >
      {item.fields.eyebrow && (
        <Eyebrow mt="$2" color={theme.contentPrimary}>
          {item.fields.eyebrow}
        </Eyebrow>
      )}
      <Text tag="h4" color={theme.contentPrimary} typography="$subheaderSmall">
        {item.fields.title}
      </Text>
      {item.fields.desc && (
        <Text tag="p" color={theme.contentPrimary} typography="$bodyLarge" display="block">
          {markdownToReactElement(item.fields.desc)}
        </Text>
      )}
      {(item.fields.url || item.fields.ctaSheet) && !item.fields.hideArrow && (
        <Box
          style={{
            position: 'absolute',
            left: '100%',
            marginTop: -8,
            top: '50%',
          }}
        >
          <IconChevronRight
            size={16}
            color={getCSSVariable('$contentTertiary', 'color')}
            strokeWidth={2}
          />
        </Box>
      )}
    </Col>
  );
};

interface ItemSheetProps {
  item: ILpComponentListItem;
  theme: IBNComponentThemeConfig;
  listItemTitleSize: '18px' | '24px';
  'aria-label'?: string;
  analyticsName?: string;
}

const ItemSheet = ({ item, listItemTitleSize, theme, ...props }: ItemSheetProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ctaRef = useRef<HTMLElement>(null);
  const exitComplete = () => {
    ctaRef.current?.focus();
  };

  return (
    <Col minWidth="$0" flexShrink={1}>
      <PressableBox
        ref={ctaRef}
        onPress={() => setIsVisible(true)}
        aria-label={props['aria-label'] || 'Open dialog'}
        analyticsName={props.analyticsName || ''}
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        $platform-web={{ textAlign: 'left' }}
        data-group
      >
        <ItemDetails item={item} theme={theme} listItemTitleSize={listItemTitleSize} />
      </PressableBox>
      <Sheet
        entry={item.fields.ctaSheet}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        exitComplete={exitComplete}
      />
    </Col>
  );
};

const RenderList = (entry: ILpComponentList) => {
  const { fields } = entry;

  const listBgColor =
    fields.listItemBg in BNColorThemes ? (fields.listItemBg as BNColorThemes) : BNColorThemes.Light;

  const layoutBgColor = getBNComponentThemeColors(
    fields.bgColor && fields.bgColor in BNColorThemes
      ? (fields.bgColor as BNColorThemes)
      : BNColorThemes.Light,
  );

  const theme = getBNComponentThemeColors(listBgColor);

  // List Item Icon Options
  const isNumbered = fields.listItemIcon === 'Number';
  const hasCheckmark = fields.listItemIcon === 'Checkmark';
  const isLight = listBgColor === BNColorThemes.Light;
  const iconBg = isLight ? getCSSVariable('$backgroundTertiary', 'color') : 'transparent';

  const iconColor = !isLight
    ? getTokenValue(theme.contentPrimary as Parameters<typeof getTokenValue>[0], 'color')
    : '#000000';

  // List Layout Options
  const headerAbove =
    fields.listLayout === 'Header Above' ||
    fields.listLayout === 'Header Above with Two Column List';
  const twoColumnList =
    fields.listLayout === 'Header Above with Two Column List' ||
    fields.listLayout === 'Header Left with Two Column List';
  const listItemTitleSize = headerAbove ? '18px' : '24px';

  return (
    <Container backgroundColor={layoutBgColor.bgPrimary}>
      <Box $largerThanSM={{ flexDirection: headerAbove ? 'column' : 'row' }} gap="$4">
        <Col flex={4} $largerThanSM={{ flexBasis: '100%' }} mb="$5" pr="$5" gap="$4">
          {fields.title && (
            <TitleWithAccent
              overRideTitleTag={fields.titleTag as 'h1' | 'h2' | 'h3'}
              title={fields.title}
              titleAccent={fields.titleAccent}
              titleSize={fields.titleSize}
              $largerThanSM={{ typography: '$headerLarge' }}
              color={layoutBgColor.contentPrimary}
            />
          )}
          <Box
            display="block"
            // @ts-expect-error "ch" units aren't accepted by bricks-next but they're valid for web styles
            $largerThanSM={{ maxWidth: headerAbove ? '60ch' : '100%' }}
          >
            {fields.subhead && (
              <Text
                tag="h4"
                display="block"
                typography={fields.titleSize === 'Large' ? '$subheaderXsmall' : '$labelMedium'}
                $largerThanSM={{
                  typography: fields.titleSize === 'Large' ? '$subheaderSmall' : '$labelMedium',
                }}
                fontWeight="$medium"
                color={layoutBgColor.inactivePrimary}
              >
                {fields.subhead}
              </Text>
            )}
            {fields.desc && <Desc>{fields.desc}</Desc>}
          </Box>
        </Col>
        <Box display="block" flex={6} $largerThanSM={{ flexBasis: '100%' }}>
          <Col
            tag={isNumbered ? 'ol' : 'ul'}
            m="$0"
            p="$0"
            gap={isLight ? '$0' : '$2'}
            role="list"
            $largerThanSM={{
              flexDirection: twoColumnList ? 'row' : 'column',
              flexWrap: twoColumnList ? 'wrap' : undefined,
            }}
            style={{ listStyle: 'none' }}
          >
            {fields.listItems?.map((item, idx) => {
              return (
                <Box
                  key={idx}
                  backgroundColor={theme.bgPrimary}
                  $platform-web={{
                    flexBasis: twoColumnList ? '47%' : 'unset',
                  }}
                  borderRadius={isLight ? undefined : '$8x'}
                  tag="li"
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  gap="$5"
                  py={isLight ? '$6' : '$7'}
                  pl={isLight ? '$0' : '$6'}
                  pr={isLight ? '$0' : '$7'}
                  borderBottomColor={isLight ? '$borderPrimary' : undefined}
                  borderBottomWidth={isLight ? '$0dot5x' : undefined}
                >
                  <ListItemIcon
                    isNumbered={isNumbered}
                    hasCheckmark={hasCheckmark}
                    isLight={isLight}
                    index={idx}
                    iconBg={iconBg}
                    iconColor={iconColor}
                  />
                  {/* eslint-disable-next-line no-nested-ternary -- Grandfathered violation */}
                  {item.fields.url ? (
                    <Link
                      href={item.fields.url}
                      aria-label=""
                      analyticsName={`cosmos-landing-page-list-item-${item.fields?.analyticsId}`}
                      variant={fields.bgColor === 'Dark' ? 'secondaryInverse' : 'primary'}
                      textDecorationLine="none"
                      data-group
                    >
                      <ItemDetails
                        item={item}
                        theme={theme}
                        listItemTitleSize={listItemTitleSize}
                      />
                    </Link>
                  ) : item.fields.ctaSheet ? (
                    <ItemSheet
                      item={item}
                      theme={theme}
                      aria-label={item.fields.ctaSheetLabel || 'Open dialog'}
                      analyticsName={`cosmos-landing-page-list-item-${item.fields?.analyticsId}`}
                      listItemTitleSize={listItemTitleSize}
                    />
                  ) : (
                    <Link
                      aria-label=""
                      analyticsName={`cosmos-landing-page-list-item-${item.fields?.analyticsId}`}
                      textDecorationLine="none"
                      data-group
                    >
                      <ItemDetails
                        item={item}
                        theme={theme}
                        listItemTitleSize={listItemTitleSize}
                      />
                    </Link>
                  )}
                </Box>
              );
            })}
          </Col>
          {fields.seeMoreLinkText && fields.seeMoreLinkUrl && (
            <Box display="block" mt="$8">
              <Link
                variant={fields.bgColor === 'Dark' ? 'secondaryInverse' : 'primary'}
                analyticsName=""
                aria-label="See More"
                href={fields.seeMoreLinkUrl}
                fontWeight="$medium"
                textDecorationLine={fields.bgColor === 'Dark' ? 'underline' : undefined}
              >
                {fields.seeMoreLinkText}
              </Link>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

const List: EntryComponent<ILpComponentList> = {
  render: RenderList,
};

export default List;
